/**
 * Client-side fastest-route optimizer.
 *
 * Uses the Google Maps JS DirectionsService with optimizeWaypoints — part of the
 * Directions API, which is already enabled for the browser key (the Distance
 * Matrix API is NOT, so we deliberately avoid DistanceMatrixService here, which
 * returns REQUEST_DENIED).
 *
 * optimizeWaypoints supports DRIVING only and up to ~25 waypoints. For transit
 * we still optimize the ORDER by driving (best available without Distance
 * Matrix) — the per-leg transit times are shown separately in the UI.
 *
 * optimizeRoute({ origin, stops, mode }) →
 *   { order: [stopId…], legs: [{toId, seconds, text}], totalSeconds, totalText }
 */

const toLatLngOrString = (p) => {
  if (p == null) return null;
  if (typeof p === 'string') return p.trim() || null;
  if (typeof p === 'object' && p.lat != null && p.lng != null) return { lat: p.lat, lng: p.lng };
  return null;
};

const fmtDur = (sec) => {
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h} hr ${mm} min` : `${h} hr`;
};

export async function optimizeRoute({ origin, stops }) {
  if (!window.google?.maps) throw new Error('Google Maps not loaded');
  const o = toLatLngOrString(origin);
  const clean = (stops || [])
    .map((s) => ({ id: s.id, loc: toLatLngOrString(s.location) }))
    .filter((s) => s.loc);
  if (!o) throw new Error('origin required');
  if (clean.length === 0) throw new Error('stops required');

  const svc = new window.google.maps.DirectionsService();
  const destination = clean[clean.length - 1].loc;
  const middle = clean.slice(0, -1);

  const res = await new Promise((resolve, reject) => {
    svc.route({
      origin: o,
      destination,
      waypoints: middle.map((s) => ({ location: s.loc, stopover: true })),
      optimizeWaypoints: true,
      travelMode: window.google.maps.TravelMode.DRIVING,
    }, (r, status) => (status === 'OK' ? resolve(r) : reject(new Error(`Directions: ${status}`))));
  });

  const legs = res.routes?.[0]?.legs || [];
  const order = res.routes?.[0]?.waypoint_order || [];
  const orderedMiddle = order.length ? order.map((i) => middle[i]) : middle;
  const orderedStops = orderedMiddle.concat([clean[clean.length - 1]]);

  let totalSeconds = 0;
  const legsOut = orderedStops.map((s, i) => {
    const sec = legs[i]?.duration?.value ?? null;
    if (sec != null) totalSeconds += sec;
    return { toId: s.id, seconds: sec, text: legs[i]?.duration?.text ?? null };
  });

  return {
    order: orderedStops.map((s) => s.id),
    legs: legsOut,
    totalSeconds,
    totalText: fmtDur(totalSeconds),
  };
}
